/**
 * Access-token refresh: exchange the httpOnly refresh cookie for a short-lived
 * access token via `POST /auth/token/refresh`.
 *
 * The refresh cookie (path `/auth`, httpOnly) rides along automatically because
 * we send `credentials: 'same-origin'`; the backend rotates it (sets a fresh
 * refresh cookie) and returns `{ accessToken, expiresIn }`, which we store in
 * the in-memory access-token store. A 401 means the refresh cookie is
 * missing/invalid/revoked — i.e. logged out.
 *
 * This calls `window.fetch` DIRECTLY, never the `lib/fetch` wrapper, so it can
 * never re-enter the Bearer-attach / 401-retry path and recurse. The endpoint is
 * guarded by `X-Requested-With` only (it is body-less — the refresh material is
 * the cookie), so we send that header and no JSON body.
 *
 * Stampede guard: concurrent callers (every in-flight `/api/*` that 401s at once
 * on app load or after the access token expires) share a SINGLE in-flight refresh
 * promise. The first caller starts the request; the rest await the same promise,
 * so the refresh cookie is rotated exactly once per burst.
 */
import { setAccessToken, clearAccessToken } from './accessToken';

/** The refresh endpoint and the CSRF header the backend's `requireRequestedWith` checks. */
const REFRESH_URL = '/auth/token/refresh';
const REQUESTED_WITH = 'Merlin';

/** The single in-flight refresh, shared by concurrent callers. `null` when idle. */
let inFlight: Promise<string> | null = null;

/** The JSON the refresh endpoint returns on success. */
interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * Perform the actual refresh request. Resolves with the new access token (also
 * written to the in-memory store) or rejects when refresh fails (401 / network).
 * On any failure the in-memory token is cleared so callers fall back to the
 * logged-out path.
 */
async function performRefresh(): Promise<string> {
  let response: Response;
  try {
    response = await window.fetch(REFRESH_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': REQUESTED_WITH,
        Accept: 'application/json',
      },
    });
  } catch (err) {
    // Network failure: drop any stale token and surface the error.
    clearAccessToken();
    throw err;
  }
  if (!response.ok) {
    clearAccessToken();
    throw new Error(`Token refresh failed with status ${response.status}`);
  }
  const data = (await response.json()) as RefreshResponse;
  setAccessToken(data.accessToken, data.expiresIn);
  return data.accessToken;
}

/**
 * Refresh the access token, deduplicating concurrent calls.
 *
 * Returns the new access token on success. On failure (refresh cookie
 * missing/invalid/revoked, or network error) the promise rejects and the
 * in-memory access token has been cleared.
 */
export function refreshAccessToken(): Promise<string> {
  if (inFlight) {
    return inFlight;
  }
  // Clear the shared promise once it settles (success OR failure) so the next
  // burst starts a fresh refresh rather than re-resolving a stale result.
  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
